import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ndfApi } from '../../services/ndf-api';
import type { Motorisation, TypeVehicule, Vehicule } from '../../types/ndf';
import {
  BoutonPrincipal,
  BoutonSecondaire,
  Champ,
  Etiquette,
  Saisie,
  SaisieNombre,
  Selection,
} from './ui/Champ';
import { MessageErreur } from './ui/MessageEtat';
import { preparerJustificatif } from './photo';
import { refusDuTypeDePiece } from '../../lib/piece-jointe';

/**
 * Véhicules d'un salarié — la pièce qui décide du TAUX des indemnités
 * kilométriques (specs B2, R-VEH-01/02).
 *
 * Trois informations seulement entrent dans le calcul : le TYPE (les tranches
 * de distance ne sont pas les mêmes pour une voiture et un deux-roues), la
 * PUISSANCE FISCALE (elle choisit la ligne du barème) et la MOTORISATION (seul
 * l'électrique majore de 20 % — l'hybride suit les taux thermiques).
 *
 * L'immatriculation et la copie de la carte grise n'entrent PAS dans le
 * calcul : elles servent au contrôle URSSAF, qui demande de pouvoir rattacher
 * les indemnités à un véhicule réel.
 *
 * R-VEH-01 : un seul véhicule actif à la fois. Activer le nouveau désactive
 * l'ancien côté serveur — on ne supprime jamais, sinon les trajets déjà
 * remboursés perdraient la référence du véhicule qui les a justifiés.
 */

const TYPES: { valeur: TypeVehicule; libelle: string }[] = [
  { valeur: 'voiture', libelle: 'Voiture' },
  { valeur: 'deux_roues_50_plus', libelle: 'Deux-roues de plus de 50 cm³' },
  { valeur: 'cyclomoteur', libelle: 'Cyclomoteur de moins de 50 cm³' },
];

const MOTORISATIONS: { valeur: Motorisation; libelle: string }[] = [
  { valeur: 'thermique', libelle: 'Thermique' },
  { valeur: 'hybride', libelle: 'Hybride' },
  { valeur: 'electrique', libelle: 'Électrique' },
];

const libelleType = (v: string) => TYPES.find((t) => t.valeur === v)?.libelle ?? v;
const libelleMotorisation = (v: string) =>
  MOTORISATIONS.find((m) => m.valeur === v)?.libelle ?? v;

interface Brouillon {
  type_vehicule: TypeVehicule;
  cv_fiscaux: number | null;
  motorisation: Motorisation;
  immatriculation: string;
  carte_grise_base64: string | null;
  carte_grise_filename: string | null;
}

function brouillonVide(): Brouillon {
  return {
    type_vehicule: 'voiture',
    cv_fiscaux: null,
    motorisation: 'thermique',
    immatriculation: '',
    carte_grise_base64: null,
    carte_grise_filename: null,
  };
}

function brouillonDe(v: Vehicule): Brouillon {
  return {
    type_vehicule: v.type_vehicule,
    cv_fiscaux: v.cv_fiscaux,
    motorisation: v.motorisation,
    immatriculation: v.immatriculation ?? '',
    carte_grise_base64: null, // on ne recharge pas la pièce déjà stockée
    carte_grise_filename: v.carte_grise_filename,
  };
}

export function VehiculesSalarie({
  salarieId,
  peutEcrire,
}: {
  salarieId: string;
  peutEcrire: boolean;
}) {
  const qc = useQueryClient();
  const [edite, setEdite] = useState<string | 'nouveau' | null>(null);
  const [brouillon, setBrouillon] = useState<Brouillon>(brouillonVide);
  const [erreurFichier, setErreurFichier] = useState<string | null>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ['ndf', 'vehicules', salarieId],
    queryFn: () => ndfApi.vehicules.liste(salarieId),
    retry: false,
  });
  const vehicules = data?.vehicules ?? [];

  const enregistrer = useMutation({
    mutationFn: async () => {
      // `carte_grise_base64` n'existe pas sur le type `Vehicule` : le serveur ne
      // renvoie JAMAIS la pièce dans une liste (c'est une pièce d'identité). Il
      // l'accepte en revanche à l'écriture, d'où ce type d'envoi distinct.
      const corps: Partial<Vehicule> & {
        salarie_id?: string;
        carte_grise_base64?: string | null;
      } = {
        type_vehicule: brouillon.type_vehicule,
        // Le cyclomoteur n'a pas de puissance fiscale : envoyer 0 ferait
        // échouer la validation, qui exige l'absence du champ.
        cv_fiscaux: brouillon.type_vehicule === 'cyclomoteur' ? null : brouillon.cv_fiscaux,
        motorisation: brouillon.motorisation,
        immatriculation: brouillon.immatriculation.trim() || null,
        actif: true,
      };
      if (brouillon.carte_grise_base64) {
        corps.carte_grise_base64 = brouillon.carte_grise_base64;
        corps.carte_grise_filename = brouillon.carte_grise_filename;
      }
      return edite === 'nouveau'
        ? ndfApi.vehicules.creer({ ...corps, salarie_id: salarieId })
        : ndfApi.vehicules.modifier(edite as string, corps);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['ndf', 'vehicules', salarieId] });
      setEdite(null);
    },
  });

  async function choisirFichier(fichier: File | undefined, champ?: HTMLInputElement | null) {
    setErreurFichier(null);
    if (!fichier) return;
    // Le serveur refuse un fichier qui s'exécuterait à la réouverture : le dire
    // ici, avec la MÊME fonction, évite de préparer la pièce pour rien.
    const refus = refusDuTypeDePiece(fichier);
    if (refus) {
      setErreurFichier(refus);
      // Le champ est VIDÉ, comme pour les arrêts de travail : sinon l'écran
      // affiche le nom du fichier refusé comme s'il était retenu.
      if (champ) champ.value = '';
      return;
    }
    try {
      // Même préparation que les justificatifs de dépense : photo d'iPhone
      // compressée, PDF transmis tel quel.
      const prepare = await preparerJustificatif(fichier);
      setBrouillon((b) => ({
        ...b,
        carte_grise_base64: prepare.contenu_base64,
        carte_grise_filename: prepare.filename,
      }));
    } catch {
      setErreurFichier('Ce fichier n’a pas pu être lu. Réessayez avec une photo ou un PDF.');
    }
  }

  return (
    <section className="mt-4 rounded-lg border border-slate-200 bg-white p-4">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div>
          <h2 className="font-titre font-bold text-nuit">Véhicule</h2>
          <p className="text-sm text-nuit/60">
            Ces informations décident du taux appliqué aux indemnités kilométriques. Sans véhicule
            actif, le salarié ne peut pas déclarer de trajet.
          </p>
        </div>
        {peutEcrire && edite === null && (
          <BoutonSecondaire
            type="button"
            onClick={() => {
              setBrouillon(brouillonVide());
              setEdite('nouveau');
            }}
          >
            {vehicules.length === 0 ? 'Ajouter un véhicule' : 'Changer de véhicule'}
          </BoutonSecondaire>
        )}
      </div>

      {isLoading && <p className="text-sm text-nuit/60">Chargement…</p>}
      {error && <MessageErreur erreur={error} />}

      {!isLoading && vehicules.length === 0 && edite === null && (
        <p className="rounded-md border border-jaune-fond/60 border-l-4 border-l-jaune-fond p-3 text-sm text-jaune">
          Aucun véhicule enregistré. Relevez le type, la puissance fiscale (case P.6 de la carte
          grise) et la motorisation (case P.3), puis ajoutez-les ici.
        </p>
      )}

      <ul className="space-y-2">
        {vehicules.map((v) => (
          <li
            key={v.id}
            className={`rounded-md border p-3 ${
              v.actif ? 'border-nuit/15' : 'border-dashed border-nuit/15 opacity-60'
            }`}
          >
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="text-sm text-nuit">
                <span className="font-medium">{libelleType(v.type_vehicule)}</span>
                {v.cv_fiscaux !== null && <> · {v.cv_fiscaux} CV</>}
                <> · {libelleMotorisation(v.motorisation)}</>
                {v.immatriculation && <> · {v.immatriculation}</>}
              </div>
              <div className="flex items-center gap-2">
                {v.actif ? <Etiquette ton="actif">Actif</Etiquette> : <Etiquette>Remplacé</Etiquette>}
                {(v.carte_grise_filename || v.carte_grise_presente) && (
                  <Etiquette>Carte grise fournie</Etiquette>
                )}
                {peutEcrire && edite === null && v.actif && (
                  <BoutonSecondaire
                    type="button"
                    onClick={() => {
                      setBrouillon(brouillonDe(v));
                      setEdite(v.id);
                    }}
                  >
                    Modifier
                  </BoutonSecondaire>
                )}
              </div>
            </div>
          </li>
        ))}
      </ul>

      {edite !== null && (
        <div className="mt-3 space-y-3 rounded-md border border-nuit/15 p-3">
          <div className="grid gap-3 sm:grid-cols-2">
            <Champ label="Type de véhicule" requis>
              <Selection
                value={brouillon.type_vehicule}
                onChange={(e) =>
                  setBrouillon((b) => ({ ...b, type_vehicule: e.target.value as TypeVehicule }))
                }
              >
                {TYPES.map((t) => (
                  <option key={t.valeur} value={t.valeur}>
                    {t.libelle}
                  </option>
                ))}
              </Selection>
            </Champ>

            {/* Le cyclomoteur de moins de 50 cm³ n'a pas de puissance fiscale :
                son barème est unique. Afficher le champ grisé inviterait à le
                remplir, et le serveur refuserait la saisie. */}
            {brouillon.type_vehicule !== 'cyclomoteur' && (
              <Champ
                label="Puissance fiscale"
                requis
                aide="Case P.6 de la carte grise. C’est elle qui choisit la ligne du barème."
              >
                <SaisieNombre
                  valeur={brouillon.cv_fiscaux}
                  onValeur={(v) => setBrouillon((b) => ({ ...b, cv_fiscaux: v }))}
                  nullable
                  inputMode="numeric"
                />
              </Champ>
            )}

            <Champ
              label="Motorisation"
              requis
              aide="Seul l’électrique majore le barème de 20 %. L’hybride suit les taux thermiques."
            >
              <Selection
                value={brouillon.motorisation}
                onChange={(e) =>
                  setBrouillon((b) => ({ ...b, motorisation: e.target.value as Motorisation }))
                }
              >
                {MOTORISATIONS.map((m) => (
                  <option key={m.valeur} value={m.valeur}>
                    {m.libelle}
                  </option>
                ))}
              </Selection>
            </Champ>

            <Champ label="Immatriculation" aide="Facultative. Sert au contrôle, pas au calcul.">
              <Saisie
                value={brouillon.immatriculation}
                onChange={(e) => setBrouillon((b) => ({ ...b, immatriculation: e.target.value }))}
                placeholder="AB-123-CD"
              />
            </Champ>
          </div>

          <Champ
            label="Copie de la carte grise"
            erreur={erreurFichier}
            aide={
              brouillon.carte_grise_filename
                ? `Pièce jointe : ${brouillon.carte_grise_filename}`
                : 'Facultative pour le calcul, mais demandée en cas de contrôle URSSAF. Photo ou PDF.'
            }
          >
            <input
              type="file"
              accept="image/*,application/pdf"
              onChange={(e) => void choisirFichier(e.target.files?.[0], e.target)}
              className="block w-full text-sm text-nuit file:mr-3 file:min-h-11 file:rounded-md file:border-0 file:bg-nuit/5 file:px-3 file:text-sm file:text-nuit"
            />
          </Champ>

          {enregistrer.error && <MessageErreur erreur={enregistrer.error} />}

          {edite === 'nouveau' && vehicules.some((v) => v.actif) && (
            <p className="text-sm text-nuit/60">
              Le véhicule actuellement actif sera marqué « Remplacé » : il reste attaché aux trajets
              déjà déclarés.
            </p>
          )}

          <div className="flex flex-wrap gap-2">
            <BoutonPrincipal
              type="button"
              onClick={() => enregistrer.mutate()}
              disabled={enregistrer.isPending}
            >
              {enregistrer.isPending ? 'Enregistrement…' : 'Enregistrer'}
            </BoutonPrincipal>
            <BoutonSecondaire type="button" onClick={() => setEdite(null)}>
              Annuler
            </BoutonSecondaire>
          </div>
        </div>
      )}
    </section>
  );
}
